import React, { useEffect, useState } from 'react';
import { Button, Input, Typography, Select, Form, Row, Col, Spin, Divider, message } from 'antd';
import {
  useGetBlockEventById,
  usePostBlockEvents,
  usePostUnblockEvents,
} from '@src/api/emp/queryHooks';
import { IBlockEventData } from '@src/common/types/empTypes';
import { useBlockedDetailPanelStore } from '@src/components/detail-panel/store';
import {
  BlockRuleTypeOptions,
  EBlockEventSingleChangeType,
  EBlockEventSingleRuleActionType,
} from '@src/components/block-event-single/constant/constant';
import { cloneDeep } from 'lodash';
import { EPanelOpenMethod } from '@src/components/block-event-single/store';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';

import TextArea from 'antd/es/input/TextArea';
import { validateBlockEvent } from '@src/components/block-event-single/utils/validateUtils';
import {
  addOrBlockRuleItem,
  deleteBlockRuleItem,
  updateBlockRules,
} from '@src/components/block-event-single/utils/handleRulesUtils';
import { EEventManagementTabsType } from '@src/common/constant/enum';
import { getDefaultBlockEventRuleItem } from '@src/components/block-event-single/utils/initializeUtils';
import ToolBar from '@src/components/detail-panel/components/ToolBar';
import EventInfoCard from '@src/components/detail-panel/components/EventInfoCard';
import { BlockedRules } from '@src/components/detail-panel/components/EventMappingRules';
import EventStatusCard from '@src/components/detail-panel/components/EventStatusCard';
import EventRelatedCard from '@src/components/detail-panel/components/EventRelatedCard';

const { Text, Title } = Typography;
const { Option } = Select;

const BlockedEventDetail = ({ record, onUnblock, onClose }) => {
  const { data: blockEventRes, isFetching: isBlockEventFetching } = useGetBlockEventById(
    record.event_id,
  );
  const { mutate: blockEvents, isLoading: isBlockLoading } = usePostBlockEvents();
  const { mutate: unblockEvents, isLoading: isUnblockLoading } = usePostUnblockEvents();

  const [
    blockType,
    blockEventData,
    blockEventDataDraft,
    setBlockType,
    setBlockEventData,
    setBlockEventDataDraft,
    setBlockEventNote,
    resetBlockedDetailPanelStore,
  ] = useBlockedDetailPanelStore((state) => [
    state.blockType,
    state.blockEventData,
    state.blockEventDataDraft,
    state.setBlockType,
    state.setBlockEventData,
    state.setBlockEventDataDraft,
    state.setBlockEventNote,
    state.resetBlockedDetailPanelStore,
  ]);

  const blockEventResData = (blockEventRes as { data: IBlockEventData })?.data;
  const eventName = blockEventResData?.event_name;
  const [mode, setMode] = useState<EPanelOpenMethod>(EPanelOpenMethod.VIEW);

  useEffect(() => {
    if (blockEventResData?.block_rules?.length > 0) {
      setBlockType(BlockRuleTypeOptions[1].value);
    } else {
      setBlockType(BlockRuleTypeOptions[0].value);
    }
    if (blockEventResData) {
      setBlockEventDataDraft(cloneDeep(blockEventResData));
      setBlockEventData(blockEventResData);
    }
  }, [blockEventRes]);

  const handleChangeEventInfo = (infoValue) => {
    const { type, value } = infoValue;
    if (type === EEventManagementTabsType.BLOCKED_EVENT) {
      setBlockEventNote(value);
    }
  };

  const handleBlockChange = (
    subAndIndex: number | null,
    subOrIndex: number | null,
    // BLOCK_EVENT_SINGLE_NAME is not included
    changeType: EBlockEventSingleChangeType,
    value: string,
  ) => {
    blockEventDataDraft &&
      setBlockEventDataDraft(
        updateBlockRules([blockEventDataDraft], 0, subAndIndex, subOrIndex, changeType, value)[0],
      );
  };

  const handleBlockTypeChange = (value) => {
    setBlockType(value);
    if (value === BlockRuleTypeOptions[1].value && blockEventDataDraft?.block_rules.length === 0) {
      blockEventDataDraft.block_rules = [[getDefaultBlockEventRuleItem()]];
      setBlockEventDataDraft(blockEventDataDraft);
    }
  };

  const handleBlockRulesChange = (
    ruleType: EBlockEventSingleRuleActionType,
    index = 0,
    subAndIndex = 0,
    subOrIndex = 0,
  ) => {
    const newBlockEventSingleDataGroup = blockEventDataDraft;
    if (newBlockEventSingleDataGroup) {
      switch (ruleType) {
        case EBlockEventSingleRuleActionType.DELETE_RULE_ITEM:
          deleteBlockRuleItem([newBlockEventSingleDataGroup], index, subAndIndex, subOrIndex);
          break;
        case EBlockEventSingleRuleActionType.ADD_OR_RULE_ITEM:
          addOrBlockRuleItem([newBlockEventSingleDataGroup], index);
          break;
        default:
          break;
      }
      setBlockEventDataDraft(newBlockEventSingleDataGroup);
    }
  };

  const handleEditClick = () => {
    setMode(EPanelOpenMethod.EDIT);
  };

  const handleCancelClick = () => {
    setMode(EPanelOpenMethod.VIEW);
    blockEventData && setBlockEventDataDraft(blockEventData);
  };

  const handleCloseClick = () => {
    resetBlockedDetailPanelStore();
    onClose?.();
  };

  const handleBlockClick = () => {
    if (blockEventDataDraft) {
      const prepareBlockEventsData = cloneDeep(blockEventDataDraft);
      // 'Block All' clears block_rules
      if (blockType === BlockRuleTypeOptions[0].value) {
        if (prepareBlockEventsData.block_rules?.length !== 0) {
          prepareBlockEventsData.block_rules = [];
        }
      }
      const res = validateBlockEvent([prepareBlockEventsData]);
      if (!res.status) {
        message.warning(`${res.key?.join(' OR ')} ${res.result}`).then();
        return;
      }
      blockEvents(
        { events: res?.data },
        {
          onSuccess: () => {
            message
              .success({
                content: (
                  <div style={{ textAlign: 'left' }}>
                    {eventName} has been blocked from sensor.
                    <br />
                    Sensors may take up to 5 minutes to reflect changes.
                  </div>
                ),
                duration: 5,
              })
              .then();
          },
          onError: (error: any) => {
            message.error(error?.response?.data?.msg).then();
          },
        },
      );
    }
  };

  const handleUnblockClick = () => {
    eventName &&
      unblockEvents(
        { events: [eventName] },
        {
          onSuccess: () => {
            message
              .success({
                content: (
                  <div style={{ textAlign: 'left' }}>
                    {eventName} has been unblocked from sensor.
                    <br />
                    Sensors may take up to 5 minutes to reflect changes.
                  </div>
                ),
                duration: 5,
              })
              .then();
            handleCloseClick();
          },
          onError: (error: any) => {
            message.error(error?.response?.data?.msg).then();
          },
        },
      );
  };
  const handleSaveClick = () => {
    setMode(EPanelOpenMethod.VIEW);
    blockEventDataDraft && setBlockEventData(blockEventDataDraft);
    handleBlockClick();
  };

  if (isBlockEventFetching) {
    return <Spin spinning style={{ width: '100%' }} />;
  }

  return (
    <div style={{ background: '#fafafa', width: '100%' }}>
      <ToolBar
        loading={isBlockLoading || isUnblockLoading}
        type={EEventManagementTabsType.BLOCKED_EVENT}
        mode={mode}
        onClose={handleCloseClick}
        onEdit={handleEditClick}
        onCancel={handleCancelClick}
        onSave={handleSaveClick}
        onUnblock={handleUnblockClick}
      />
      <EventInfoCard
        mode={mode}
        type={EEventManagementTabsType.BLOCKED_EVENT}
        blockEvent={blockEventDataDraft}
        onChangeEventInfo={handleChangeEventInfo}
      />
      <BlockedRules
        mode={mode}
        data={blockEventDataDraft}
        blockType={blockType}
        onChange={handleBlockChange}
        onBlockTypeChange={handleBlockTypeChange}
        onBlockRulesChange={handleBlockRulesChange}
      />
    </div>
  );
};

export default BlockedEventDetail;
